'use client'

import { useState, useEffect, useRef, cloneElement, isValidElement } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { cva, type VariantProps } from 'class-variance-authority'

import Link from './Link'

import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'
import { type MenuItem } from '@/helpers/menus'
import ScrollView from './ScrollView'
import { easeShort } from '@/lib/transitions'

const variants = cva(
  'absolute w-fit before:absolute before:shadow-xl before:w-[calc(100%-4px)] before:border before:z-0 before:inset-0 before:rounded-xl text-gray-500 before:bg-white before:border-gray-200 dark:text-gray-500 dark:before:bg-neutral-950 dark:before:border-white/10',
  {
    variants: {
      position: {
        down: 'top-full mt-1',
        up: 'bottom-full mb-1',
        center: 'top-1/2',
        left: '-top-10 right-full mr-3',
      },
      align: {
        left: '',
        right: 'right-0 before:left-1',
      },
      isSub: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        position: 'down',
        align: 'right',
        isSub: true,
        className: 'right-full',
      },
      {
        position: 'down',
        align: 'left',
        isSub: true,
        className: 'left-full',
      },
      {
        position: 'up',
        align: 'right',
        isSub: true,
        className: 'right-full',
      },
      {
        position: 'up',
        align: 'left',
        isSub: true,
        className: 'left-full',
      },
      {
        position: 'center',
        align: 'left',
        className: 'left-full',
      },
      {
        position: 'center',
        align: 'right',
        className: 'right-full',
      },
      {
        position: 'left',
        align: 'right',
        className: 'right-full',
      },
    ],
    defaultVariants: {
      position: 'down',
      align: 'left',
    },
  },
)

type DropdownMenuProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof variants> & {
    menu: MenuItem[]
    position?: 'up' | 'down' | 'center' | 'left'
    align?: 'left' | 'right'
    subAlign?: 'up' | 'down' | 'center'
    size?: number
    setVisible: (visible: boolean) => void
    showArrow?: boolean
    isSub?: boolean
    shouldScroll?: boolean
    maxHeight?: number
    minWidth?: number
  }

type PopoverProps = DropdownMenuProps & {
  visible: boolean
  setVisible: (visible: boolean) => void
}

export default function Popover({
  menu,
  position,
  align,
  subAlign,
  children,
  visible,
  setVisible,
  size,
  shouldScroll,
  maxHeight,
  className,
}: PopoverProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visible) {
        setVisible(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [setVisible, visible])

  return (
    <>
      <div
        style={{ height: size ? `${size}px` : `auto ` }}
        className={cn('relative', visible ? 'z-[99]' : 'z-auto')}
      >
        <div onClick={() => setVisible(!visible)}>{children}</div>
        <AnimatePresence>
          {visible && (
            <DropdownMenu
              menu={menu}
              position={position}
              align={align}
              subAlign={subAlign}
              className={className}
              setVisible={setVisible}
              minWidth={200}
              shouldScroll={shouldScroll}
              maxHeight={maxHeight}
            />
          )}
        </AnimatePresence>
      </div>
      {visible && (
        <div
          onClick={() => setVisible(false)}
          className="fixed left-0 top-0 z-40 h-screen w-screen"
        ></div>
      )}
    </>
  )
}

export function DropdownMenu({
  menu,
  position,
  align,
  subAlign = 'center',
  minWidth = 0,
  setVisible,
  showArrow,
  isSub,
  shouldScroll,
  maxHeight,
  className,
}: DropdownMenuProps) {
  const [subVisible, setSubVisible] = useState<number | null>(null)
  const [copied, setCopied] = useState(false)
  const subCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (subCloseTimer.current) {
        clearTimeout(subCloseTimer.current)
      }
    }
  }, [])

  const openSub = (index: number) => {
    if (subCloseTimer.current) {
      clearTimeout(subCloseTimer.current)
      subCloseTimer.current = null
    }
    setSubVisible(index)
  }

  const closeSub = () => {
    subCloseTimer.current = setTimeout(() => {
      setSubVisible(null)
    }, 150)
  }

  const cancelClose = () => {
    if (subCloseTimer.current) {
      clearTimeout(subCloseTimer.current)
      subCloseTimer.current = null
    }
  }

  const copyLink = (link?: string) => {
    const currentUrl = `${window.location.origin}${link ?? window.location.pathname}`
    navigator.clipboard.writeText(`${currentUrl}` || '')
    setCopied(true)

    setTimeout(() => {
      setVisible(false)
      setCopied(false)
    }, 2000)
  }

  const yInitial =
    position === 'center' ? 'calc(-50% + 10px)' : isSub ? (position === 'up' ? 38 : -38) : 10
  const yAnimate = position === 'center' ? '-50%' : isSub ? (position === 'up' ? 48 : -48) : 0

  const items = menu.map((item, index) => {
    const hasSub = item.sub && item.sub.length > 0
    const isCustom = item.custom

    if (item.label === 'Divider') {
      return (
        <li
          key={`divider-${index}`}
          className={cn(
            'relative z-10 w-[calc(100%-4px)] py-1.5',
            align === 'right' ? 'ml-1' : align === 'left' ? 'mr-1' : '',
          )}
        >
          <hr className="h-px w-full border-0 bg-gray-200 dark:bg-white/10"></hr>
        </li>
      )
    }

    const option = (label?: any, icon?: any, key?: any) => {
      return (
        <div
          className={cn(
            'group w-full min-w-40 px-3 py-1',
            index === 0 && 'pt-3',
            index === menu.length - 1 && 'pb-3',
          )}
        >
          <div
            className={cn(
              'flex h-7 w-full items-center gap-3 whitespace-nowrap rounded-lg pl-1 pr-2 text-13 font-medium transition-colors',
              item.disabled
                ? 'cursor-default select-none'
                : `cursor-pointer group-hover:bg-primary-50 group-hover:text-primary-500 dark:group-hover:bg-primary-500/10`,
            )}
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={key}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: item.disabled ? 0.5 : 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ duration: 0.1 }}
                className="align-center flex h-5 w-5 shrink-0 items-center justify-center"
              >
                {typeof (icon ?? item.icon) === 'string' ? (
                  <Icon name={icon ?? item.icon} size={14} />
                ) : (
                  icon ?? item.icon
                )}
              </motion.span>
            </AnimatePresence>
            <p
              className={cn(
                'mr-2 flex shrink gap-[5px] truncate',
                item.disabled && 'text-gray-400',
              )}
            >
              {label ?? item.label}
            </p>
            {item.selected && <Icon name="check" size={12} className="ml-auto text-primary-500" />}
            {(hasSub || isCustom) && <Icon name="caret-right" size={10} className="ml-auto" />}
            {showArrow && <Icon name="arrow-left" size={12} className="ml-auto rotate-180" />}
          </div>
        </div>
      )
    }

    const containerClass = `relative w-full ${align === 'right' ? 'pl-1' : 'pr-1'}`

    if (item.disabled) {
      return (
        <li key={index} className={containerClass}>
          {option()}
        </li>
      )
    }
    if (item.link && item.label !== 'Share') {
      const handleClick = () => {
        if (item.onClick) {
          item.onClick()
        }
        if (item?.simultaneousClick) {
          item?.simultaneousClick()
        }
        setVisible(false)
      }

      return (
        <li key={index} className={containerClass}>
          <Link
            href={item.link}
            onClick={handleClick}
            className="w-full"
            passReferrerUrl={item.passReferrerUrl}
            sponsored={item.sponsored}
          >
            {option()}
          </Link>
        </li>
      )
    } else if (item.onClick) {
      const handleClick = () => {
        if (item.onClick) {
          item.onClick()
        }
        setVisible(false)
      }

      return (
        <li key={index} className={containerClass}>
          <button
            key={index}
            type="button"
            onClick={handleClick}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleClick()
            }}
            className="w-full"
          >
            {option()}
          </button>
        </li>
      )
    } else if (item.label === 'Share') {
      return (
        <li key={index} className={containerClass}>
          <button
            type="button"
            onClick={() => copyLink(item.link ?? undefined)}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => {
              e.preventDefault()
              e.stopPropagation()
              copyLink(item.link ?? undefined)
            }}
            className="w-full"
          >
            {option(
              copied ? 'Link copied' : item.label,
              copied ? <Icon name="check" /> : item.icon,
              copied,
            )}
          </button>
        </li>
      )
    } else if (hasSub || isCustom) {
      return (
        <li key={index} className={containerClass}>
          <button
            type="button"
            className="w-full text-left"
            aria-haspopup="menu"
            aria-expanded={subVisible === index}
            onMouseEnter={() => openSub(index)}
            onMouseLeave={closeSub}
          >
            {option()}
          </button>
          <AnimatePresence>
            {subVisible === index &&
              (isCustom ? (
                <motion.div
                  initial={{ opacity: 0, y: 'calc(-50% + 10px)' }}
                  animate={{ opacity: 1, y: '-50%' }}
                  exit={{ opacity: 0, y: 'calc(-50% + 10px)' }}
                  transition={easeShort}
                  onMouseEnter={cancelClose}
                  className={cn(
                    'absolute z-50 w-fit',
                    align === 'left' && 'left-full',
                    align === 'right' && 'right-full',
                    'top-1/2',
                  )}
                >
                  {isValidElement(item.custom)
                    ? cloneElement(item.custom, {
                        horizontalAlign: align,
                        verticalAlign: 'bottom',
                      } as React.Attributes)
                    : item.custom}
                </motion.div>
              ) : (
                <div onMouseEnter={cancelClose} onTouchStart={(e) => e.stopPropagation()}>
                  <DropdownMenu
                    menu={item.sub ?? []}
                    position={subAlign}
                    align={align}
                    subAlign={subAlign}
                    setVisible={setVisible}
                    isSub={true}
                    minWidth={220}
                    shouldScroll={shouldScroll}
                  />
                </div>
              ))}
          </AnimatePresence>
        </li>
      )
    }

    return null
  })

  return (
    <motion.ul
      initial={{ opacity: 0, y: yInitial }}
      animate={{ opacity: 1, y: yAnimate }}
      exit={{ opacity: 0, y: yInitial }}
      transition={easeShort}
      className={cn(variants({ position, align, isSub, className }))}
      style={{ minWidth: minWidth }}
    >
      {maxHeight || (isSub && (shouldScroll || items.length > 15)) ? (
        <ScrollView
          direction="vertical"
          className="h-fit"
          style={{ maxHeight: `${maxHeight ?? 360}px` }}
          radius="rounded-xl"
          enhance
        >
          {items}
        </ScrollView>
      ) : (
        items
      )}
    </motion.ul>
  )
}
